import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    ApFeedProducts,
    ApFeedRequest,
    ApFeedResponse,
    ApFeedUpdateRequest,
    ApProduct,
    DiscoverProviderResponse,
    FtpFeedRequest,
    FtpFeedResponse,
    FtpFeedUpdateRequest,
    WireCredentials,
    WireCredentialsRequest,
} from '@discover/store/models/feed.provider.models';
import { WireFeedTypes } from '@src/suite/common/enums/wire-feed-types';
import { map, Observable, throwError } from 'rxjs';

import { GlobalTablesService } from './global-tables-service';
import { NomWebServicesJson } from './nom-web-services-json';

@Injectable({ providedIn: 'root' })
export class FeedProviderService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService
    ) {}

    public createWireCredentials(credentials: WireCredentialsRequest): Observable<WireCredentials> {
        let response: Observable<WireCredentials> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials?feedType=' + credentials.feedType;

        try {
            response = this.nomWebServicesJson
                .discoverServicePost<WireCredentials>(url, credentials, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        if (!res.body.feedType) res.body.feedType = credentials.feedType;
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getWireCredentials(feedType: WireFeedTypes, credentialsId?: string): Observable<Array<WireCredentials>> {
        let response: Observable<Array<WireCredentials>> = null;
        let url: string = null;
        if (feedType) {
            url = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials?feedType=' + feedType;
        }
        if (credentialsId) {
            if (url) {
                url = url + '&id=' + credentialsId;
            } else {
                url = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials?id=' + credentialsId;
            }
        }

        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<Array<WireCredentials>>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public updateWireCredentials(feedType: WireFeedTypes, credentials: WireCredentials, credentialsChanged: boolean): Observable<WireCredentials> {
        let response: Observable<WireCredentials> = null;
        let url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials/credentials?feedType=' + feedType;
        if (!credentialsChanged) {
            url = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials?feedType=' + feedType;
            delete credentials.credentials;
        }

        try {
            response = this.nomWebServicesJson
                .discoverServicePut<WireCredentials>(url, credentials, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public deleteWireCredentials(credentialsId: string, type: WireFeedTypes): Observable<WireCredentials> {
        let response: Observable<WireCredentials> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials/' + credentialsId + '?feedType=' + type;

        try {
            response = this.nomWebServicesJson
                .discoverServiceDelete<WireCredentials>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getApFeedProducts(id: string): Observable<Array<ApProduct>> {
        let response: Observable<Array<ApProduct>> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds/products?ID=' + id;
        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<ApFeedProducts>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        const products: Array<ApProduct> = [];
                        res.body.data.plans.forEach(plan => {
                            plan.entitlements.forEach(product => {
                                if (!products.find(p => p.id === product.id)) {
                                    products.push(product);
                                }
                            });
                        });
                        return products;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public createAPFeed(feed: ApFeedRequest): Observable<ApFeedResponse> {
        let response: Observable<ApFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds';

        try {
            response = this.nomWebServicesJson
                .discoverServicePost<ApFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public updateAPFeed(feed: ApFeedUpdateRequest): Observable<ApFeedResponse> {
        let response: Observable<ApFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds';

        try {
            response = this.nomWebServicesJson
                .discoverServicePut<ApFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getAPFeeds(): Observable<Array<ApFeedResponse>> {
        let response: Observable<Array<ApFeedResponse>> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds?scope=0';
        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<Array<ApFeedResponse>>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            console.log(error);
            return throwError(() => error);
        }
        return response;
    }

    public deleteAPFeed(feedId: string, scopes: Array<number>): Observable<ApFeedResponse> {
        let response: Observable<ApFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds/' + feedId + '?scope=' + scopes[0];

        try {
            response = this.nomWebServicesJson
                .discoverServiceDelete<ApFeedResponse>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getApFeed(feedId: string): Observable<Array<ApFeedResponse>> {
        let response: Observable<Array<ApFeedResponse>> = null;
        const url: string = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/ap-feeds?id=${feedId}&scope=0`;

        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<Array<ApFeedResponse>>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public createFTPFeed(feed: FtpFeedRequest): Observable<FtpFeedResponse> {
        let response: Observable<FtpFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ftp-feeds';

        try {
            response = this.nomWebServicesJson
                .discoverServicePost<FtpFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public updateFTPFeed(feed: FtpFeedUpdateRequest): Observable<FtpFeedResponse> {
        let response: Observable<FtpFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ftp-feeds';

        try {
            response = this.nomWebServicesJson
                .discoverServicePut<FtpFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getFTPFeeds(): Observable<Array<FtpFeedResponse>> {
        let response: Observable<Array<FtpFeedResponse>> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ftp-feeds?scope=0';
        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<Array<FtpFeedResponse>>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public deleteFTPFeed(feedId: string, scopes: Array<number>): Observable<FtpFeedResponse> {
        let response: Observable<FtpFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ftp-feeds/' + feedId + '?scope=' + scopes[0];

        try {
            response = this.nomWebServicesJson
                .discoverServiceDelete<FtpFeedResponse>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getFTPFeed(feedId: string): Observable<Array<FtpFeedResponse>> {
        let response: Observable<Array<FtpFeedResponse>> = null;
        const url: string = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/ftp-feeds?id=${feedId}&scope=0`;

        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<Array<FtpFeedResponse>>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getProviders(type?: string): Observable<Array<DiscoverProviderResponse>> {
        let response: Observable<Array<DiscoverProviderResponse>> = null;
        let url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/providers';
        if (type) url += '?type=' + type;

        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<Array<DiscoverProviderResponse>>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }
}
